import { ChevronDown, ChevronRight } from "lucide-react";
import { type ReactNode, useEffect, useId, useState } from "react";

import styles from "./collapsible-section.module.css";

export type CollapsibleSectionProps = {
  summary: ReactNode;
  children: ReactNode;
  storageKey?: string;
  className?: string;
  contentClassName?: string;
};

function readExpanded(storageKey?: string): boolean {
  if (!storageKey || typeof window === "undefined") return true;
  try {
    const saved = window.localStorage.getItem(storageKey);
    return saved === null ? true : saved === "true";
  } catch {
    return true;
  }
}

export function CollapsibleSection({ summary, children, storageKey, className, contentClassName }: CollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(() => readExpanded(storageKey));
  const contentId = useId();

  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, String(expanded));
    } catch {
      // Storage may be unavailable in private or restricted browser contexts.
    }
  }, [expanded, storageKey]);

  return (
    <section className={className}>
      <div className={styles.summary}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls={contentId}
          aria-label={expanded ? "Collapse section" : "Expand section"}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        </button>
        <div className={styles.summaryContent}>{summary}</div>
      </div>
      {expanded && <div id={contentId} className={contentClassName}>{children}</div>}
    </section>
  );
}
